<template>
    <form action="">
        <div class="modal-card calendar-modal-card">
            <!-- Header -->
            <header class="modal-card-head calendar-head">
                <div class="calendar-head-content">
                    <div class="calendar-head-icon">
                        <i class="fas fa-calendar-alt"></i>
                    </div>
                    <div>
                        <p class="modal-card-title calendar-title">Task Calendar</p>
                        <p class="calendar-subtitle">{{ currentMonthLabel }}</p>
                    </div>
                </div>
                <button class="calendar-close-btn" type="button" @click="$parent.$parent.close()">
                    <i class="fas fa-times"></i>
                </button>
            </header>

            <!-- Body: two-column layout -->
            <section class="modal-card-body calendar-body">
                <!-- Left: calendar -->
                <div class="calendar-left">
                    <Calendar class="vc-calendar-custom" :attributes="calendarAttributes"
                        @update:from-page="onMonthChanged" @dayclick="onDayClick" />
                    <!-- Legend -->
                    <div class="calendar-legend">
                        <span class="legend-dot"></span>
                        <span class="legend-text">Task due date</span>
                    </div>
                </div>

                <!-- Right: task list for selected day or full month -->
                <div class="calendar-right">
                    <div class="task-panel-header">
                        <span v-if="selectedDay">
                            <i class="fas fa-list-check"></i>
                            {{ selectedDayLabel }}
                        </span>
                        <span v-else>
                            <i class="fas fa-list-check"></i>
                            All tasks this month
                        </span>
                    </div>

                    <!-- Loading -->
                    <div v-if="loading" class="task-loading">
                        <div class="task-loading-spinner"></div>
                        <span>Loading tasks…</span>
                    </div>

                    <!-- Empty state -->
                    <div v-else-if="visibleTasks.length === 0" class="task-empty">
                        <i class="fas fa-inbox"></i>
                        <p>No tasks found</p>
                        <span v-if="selectedDay">for this day</span>
                        <span v-else>for this month</span>
                    </div>

                    <!-- Task cards -->
                    <div v-else class="task-list">
                        <div v-for="task in visibleTasks" :key="task.id" class="task-card" :class="taskCardClass(task)">
                            <div class="task-card-dot" :class="taskDotClass(task)"></div>
                            <div class="task-card-content">
                                <p class="task-card-title">{{ task.title }}</p>
                                <p class="task-card-date">
                                    <i class="fas fa-clock"></i>
                                    Due: {{ formatDate(task.due_date) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </form>
</template>

<script>
import { Calendar } from 'v-calendar';
import { mapActions, mapGetters } from 'vuex';

export default {
    components: { Calendar },

    props: {
        projectId: { required: false }
    },

    data() {
        return {
            tasks: {},
            tasksForMonth: [],
            monthAndYear: '',
            selectedDay: null,
            loading: false,
        }
    },

    mounted() {
        this.getTasksForMonth()
    },

    methods: {
        ...mapActions(['fetchTasks']),

        onMonthChanged({ month, year }) {
            this.selectedDay = null
            this.getTasksForMonth(`${month}/${year}`)
        },

        onDayClick(day) {
            const date = day.date
            const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
            this.selectedDay = this.selectedDay === key ? null : key
        },

        getTasksForMonth(monthAndYear = null) {
            this.monthAndYear = monthAndYear || this.currentMonthAndYear

            if (this.tasks[this.monthAndYear]) {
                this.tasksForMonth = this.tasks[this.monthAndYear]
                return
            }

            this.loading = true
            this.fetchTasks({ monthAndYear: this.monthAndYear, project_id: this.projectId })
                .then(res => {
                    this.tasksForMonth = this.tasks[this.monthAndYear] = _.map(res.data.data, 'id')
                    return res
                })
                .finally(() => { this.loading = false })
        },

        formatDate(dateStr) {
            if (!dateStr) return '—'
            return moment(dateStr).format('MMM D, YYYY')
        },

        taskCardClass(task) {
            if (!this.selectedDay) return ''
            const due = new Date(task.due_date)
            const key = `${due.getFullYear()}-${due.getMonth()}-${due.getDate()}`
            return key === this.selectedDay ? 'task-card--selected' : ''
        },

        taskDotClass(task) {
            const due = new Date(task.due_date)
            const now = new Date()
            if (due < now) return 'dot--overdue'
            const diff = (due - now) / (1000 * 60 * 60 * 24)
            if (diff <= 3) return 'dot--soon'
            return 'dot--ok'
        }
    },

    computed: {
        ...mapGetters(['tasksByIds']),

        currentMonthAndYear() {
            const today = new Date()
            return `${today.getMonth() + 1}/${today.getFullYear()}`
        },

        currentMonthLabel() {
            if (!this.monthAndYear) return ''
            const [m, y] = this.monthAndYear.split('/')
            return moment(`${y}-${m}-01`).format('MMMM YYYY')
        },

        selectedDayLabel() {
            if (!this.selectedDay) return ''
            const [y, m, d] = this.selectedDay.split('-')
            return moment(`${y}-${parseInt(m) + 1}-${d}`, 'YYYY-M-D').format('MMMM D, YYYY')
        },

        allTasks() {

            return this.tasksByIds(this.tasksForMonth)
        },

        visibleTasks() {
            if (!this.selectedDay) return this.allTasks
            return this.allTasks.filter(task => {
                const due = new Date(task.due_date)
                const key = `${due.getFullYear()}-${due.getMonth()}-${due.getDate()}`
                return key === this.selectedDay
            })
        },

        calendarAttributes() {
            return this.allTasks.map(task => ({
                key: task.id,
                dot: { color: this.attrDotColor(task) },
                dates: new Date(task.due_date),
                popover: { label: task.title },
            }))
        },

        attrDotColor() {
            return (task) => {
                const due = new Date(task.due_date)
                const now = new Date()
                if (due < now) return 'red'
                const diff = (due - now) / (1000 * 60 * 60 * 24)
                if (diff <= 3) return 'yellow'
                return 'green'
            }
        }
    }
}
</script>

<style scoped>
/* ── Modal shell ─────────────────────────────── */
.calendar-modal-card {
    width: 820px !important;
    max-width: 96vw;
    border-radius: 16px;
    overflow: hidden;
    border: none;
    box-shadow: 0 24px 64px rgba(0, 0, 0, .18);
}

/* ── Header ──────────────────────────────────── */
.calendar-head {
    background: linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 100%);
    border-bottom: none;
    padding: 20px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.calendar-head-content {
    display: flex;
    align-items: center;
    gap: 14px;
}

.calendar-head-icon {
    width: 44px;
    height: 44px;
    background: rgba(255, 255, 255, .15);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: #fff;
}

.calendar-title {
    color: #fff !important;
    font-size: 1.1rem !important;
    font-weight: 700 !important;
    margin: 0 !important;
    line-height: 1.2;
}

.calendar-subtitle {
    color: rgba(255, 255, 255, .65);
    font-size: .78rem;
    margin-top: 2px;
}

.calendar-close-btn {
    background: rgba(255, 255, 255, .12);
    border: none;
    color: #fff;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .2s;
}

.calendar-close-btn:hover {
    background: rgba(255, 255, 255, .25);
}

/* ── Body layout ─────────────────────────────── */
.calendar-body {
    display: flex;
    gap: 0;
    padding: 0 !important;
    min-height: 380px;
    background: #f8f9fb;
}

/* ── Left panel ──────────────────────────────── */
.calendar-left {
    padding: 24px 20px 20px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    background: #fff;
    border-right: 1px solid #e8ebf0;
    min-width: 290px;
}

/* Override v-calendar to feel native */
.calendar-left :deep(.vc-container) {
    border: none;
    box-shadow: none;
    font-family: inherit;
    background: transparent;
}

.calendar-left :deep(.vc-header) {
    padding: 4px 0 8px;
}

.calendar-left :deep(.vc-title) {
    font-weight: 700;
    font-size: .95rem;
    color: #1e3a5f;
}

.calendar-left :deep(.vc-day-content:hover) {
    background: #e8f0f9;
    border-radius: 50%;
}

.calendar-left :deep(.vc-highlights .vc-day-content) {
    background: #2d6a9f;
    color: #fff;
    border-radius: 50%;
}

.calendar-legend {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: .75rem;
    color: #8a97a8;
}

.legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #2d6a9f;
}

/* ── Right panel ─────────────────────────────── */
.calendar-right {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 20px 20px 20px 20px;
    overflow: hidden;
}

.task-panel-header {
    font-size: .8rem;
    font-weight: 700;
    color: #1e3a5f;
    letter-spacing: .04em;
    text-transform: uppercase;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 7px;
}

.task-panel-header i {
    color: #2d6a9f;
}

/* Loading */
.task-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 12px;
    color: #8a97a8;
    font-size: .85rem;
}

.task-loading-spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #e0e6ee;
    border-top-color: #2d6a9f;
    border-radius: 50%;
    animation: spin .7s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Empty */
.task-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 6px;
    color: #b0bac8;
    text-align: center;
}

.task-empty i {
    font-size: 2rem;
    margin-bottom: 4px;
}

.task-empty p {
    font-weight: 600;
    color: #7a8899;
    font-size: .9rem;
}

.task-empty span {
    font-size: .78rem;
}

/* Task list */
.task-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    max-height: 310px;
    padding-right: 4px;
}

.task-list::-webkit-scrollbar {
    width: 4px;
}

.task-list::-webkit-scrollbar-track {
    background: transparent;
}

.task-list::-webkit-scrollbar-thumb {
    background: #d0d8e4;
    border-radius: 2px;
}

/* Task card */
.task-card {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #fff;
    border-radius: 10px;
    padding: 11px 13px;
    border: 1px solid #e8ebf0;
    transition: box-shadow .18s, border-color .18s;
    cursor: default;
}

.task-card:hover {
    box-shadow: 0 2px 10px rgba(30, 58, 95, .08);
    border-color: #c5d3e0;
}

.task-card--selected {
    border-color: #2d6a9f;
    box-shadow: 0 0 0 2px rgba(45, 106, 159, .15);
}

.task-card-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    margin-top: 5px;
    flex-shrink: 0;
}

.dot--ok {
    background: #38b27a;
}

.dot--soon {
    background: #f0a500;
}

.dot--overdue {
    background: #e84040;
}

.task-card-title {
    font-size: .85rem;
    font-weight: 600;
    color: #1e3a5f;
    line-height: 1.3;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 340px;
}

.task-card-date {
    font-size: .73rem;
    color: #8a97a8;
    margin: 3px 0 0;
    display: flex;
    align-items: center;
    gap: 4px;
}
</style>
